import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Card } from "@feed/shared";
import { CardBlock } from "../components/CardBlock";
import { DetailSheet } from "../components/DetailSheet";
import { BookmarkIcon, CloseIcon } from "../components/icons";
import { Screen } from "../components/Screen";
import { useDocumentTitle } from "../lib/title";
import { useCardList } from "../lib/useCardList";
import { useInfiniteScroll } from "../lib/useInfiniteScroll";
import { useStatusChange } from "../lib/useStatusChange";

export function Saved() {
  const { t } = useTranslation();
  useDocumentTitle(t("saved.title"));
  const list = useCardList({ status: "saved" });
  const change = useStatusChange(list);
  const [openId, setOpenId] = useState<string | null>(null);

  // A card that was just unsaved or dismissed leaves this list, except while its sheet is open.
  const shown = list.cards.filter((card) => card.id === openId || card.status === "saved");
  const open = list.cards.find((card) => card.id === openId) ?? null;
  const sentinel = useInfiniteScroll(list.hasMore, list.loadMore, shown.length);
  const count = `${shown.length}${list.hasMore ? "+" : ""}`;

  const move = (card: Card, status: "read" | "dismissed") =>
    change(card, status, { message: status === "read" ? t("toast.unsaved") : undefined });

  return (
    <Screen>
      <header className="page-head">
        <div>
          <h1 className="title" tabIndex={-1}>{t("saved.title")}</h1>
          <p className="subtitle">{t("saved.count", { count })}</p>
        </div>
      </header>

      {list.error && (
        <div className="notice" role="alert">
          <span>{t("errors.load")}</span>
          <button type="button" className="link-button" onClick={list.reload}>
            {t("common.retry")}
          </button>
        </div>
      )}
      {list.loading && <p className="status">{t("loading")}</p>}
      {!list.loading && !list.error && shown.length === 0 && <p className="status">{t("saved.empty")}</p>}

      <div className="stack">
        {shown.map((card) => (
          <CardBlock
            key={card.id}
            card={card}
            onOpen={() => setOpenId(card.id)}
            timeLabel={(when) => t("saved.when", { when })}
            actions={
              <>
                <button type="button" className="pill pill-solid" onClick={() => void move(card, "read")}>
                  <BookmarkIcon filled />
                  {t("saved.unsave")}
                </button>
                <button type="button" className="pill pill-outline" onClick={() => void move(card, "dismissed")}>
                  <CloseIcon />
                  {t("saved.dismiss")}
                </button>
              </>
            }
          />
        ))}
      </div>
      {list.hasMore && <div ref={sentinel} className="sentinel" aria-hidden="true" />}
      {list.loadingMore && <p className="status">{t("loading")}</p>}

      {open && (
        <DetailSheet
          card={open}
          onClose={() => setOpenId(null)}
          onSetStatus={(next) => {
            setOpenId(null);
            void change(open, next, { message: next === "read" ? t("toast.unsaved") : undefined });
          }}
        />
      )}
    </Screen>
  );
}
